import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth-context";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";

const PLANS = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    bullets: ["3 audits / 30 days", "Score overview only", "No PDF / JSON export", "No prioritised recommendations"],
  },
  {
    key: "pro",
    name: "Pro",
    price: "$19",
    period: "/ 30 days",
    bullets: ["10 audits / 30 days", "PDF + JSON export", "Full prioritised recommendations", "Audit history & comparisons"],
    accent: true,
    note: "7-day free trial — no card",
  },
  {
    key: "agency",
    name: "Agency",
    price: "$49",
    period: "/ 30 days",
    bullets: ["Unlimited audits", "Everything in Pro", "Priority queue", "Coming: scheduled re-audits"],
  },
];

const CREDITS = [
  { count: 10, price: "$5", per: "$0.50" },
  { count: 50, price: "$20", per: "$0.40", best: "Save 20%" },
  { count: 200, price: "$60", per: "$0.30", best: "Save 40%" },
];

// Plan feature comparison matrix rendered right under the pricing cards.
// Each row is `[label, free, pro, agency]`. Values may be:
//   true        → green check
//   false       → grey × (not included)
//   string/jsx  → rendered as text (centred)
// Groups break the rows into visually distinct sections.
const COMPARE_GROUPS = [
  {
    title: "Volume & runtime",
    rows: [
      ["Audits included",                "3 / 30 days",  "10 / 30 days",  "Unlimited"],
      ["Queue priority",                 "Standard",     "Standard",      "Priority"],
      ["Audit history retention",        "30 days",      "12 months",     "Unlimited"],
      ["Re-audit any URL",               true,           true,            true],
      ["Top-up with credits",            true,           true,            true],
    ],
  },
  {
    title: "The 60-point inspection",
    rows: [
      ["SEO checks (11)",                true,           true,            true],
      ["GEO — AI search checks (10)",    true,           true,            true],
      ["AEO — answer-engine checks (9)", true,           true,            true],
      ["Performance checks (8)",         true,           true,            true],
      ["Security checks (11)",           true,           true,            true],
      ["Accessibility checks (7)",       true,           true,            true],
      ["BuiltWith-style tech stack",     true,           true,            true],
    ],
  },
  {
    title: "Reporting & export",
    rows: [
      ["On-screen audit report",         "Score overview", "Full report", "Full report"],
      ["Prioritised AI recommendations", false,          true,            true],
      ["Branded PDF export",             false,          true,            true],
      ["JSON export (API-friendly)",     false,          true,            true],
    ],
  },
  {
    title: "Team & automation",
    rows: [
      ["Email support",                  "Community",    "Email",         "Priority email"],
      ["Account seats",                  "1",            "1",             "5 (workspaces)"],
      ["Scheduled re-audits (weekly / monthly)", false,  false,           true],
      ["Webhook on audit completion",    false,          false,           true],
    ],
  },
];

function CompareCell({ value }) {
  if (value === true)  return <Check size={18} weight="bold" className="text-success mx-auto" />;
  if (value === false) return <X size={16} weight="bold" className="text-ink-muted/50 mx-auto" />;
  return <span className="text-sm text-ink mono">{value}</span>;
}

export default function PricingPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/app/billing" : "/register";

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader active="pricing" />

      <section className="border-b border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="label-overline mb-4">Pricing</div>
            <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[1] mb-6">
              Simple, fair, <span className="text-brand">no auto-renewals.</span>
            </h1>
            <p className="text-lg text-ink-muted leading-relaxed max-w-2xl">
              Pay only when you need more. Every paid pass is a 30-day burst —
              renew whenever it suits you. Or top up with credits that never
              expire.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid md:grid-cols-3 gap-4">
            {PLANS.map((p) => (
              <div key={p.key} data-testid={`pricing-plan-${p.key}`}
                className={`relative p-6 lg:p-8 rounded-sm border bg-white flex flex-col ${p.accent ? "border-ink hard-shadow" : "border-border"}`}>
                {p.accent && (
                  <span className="absolute -top-3 left-6 mono text-[10px] tracking-widest font-bold bg-brand text-white px-2 py-1 rounded-sm">
                    MOST POPULAR
                  </span>
                )}
                <div className="label-overline mb-2">{p.name}</div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="num-display text-6xl">{p.price}</span>
                  <span className="text-sm text-ink-muted">{p.period}</span>
                </div>
                {p.note && <div className="mono text-xs text-success mb-4">{p.note}</div>}
                <ul className="space-y-2 mb-8 mt-4 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-success mt-0.5 shrink-0" weight="bold" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to={ctaTo} data-testid={`pricing-cta-${p.key}`}
                  className={`inline-flex items-center justify-center gap-2 py-3 rounded-sm font-semibold transition-colors ${
                    p.accent ? "bg-ink text-white hover:bg-brand" : "border border-ink text-ink hover:bg-ink hover:text-white"
                  }`}>
                  Get started <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-secondary" data-testid="pricing-compare">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="mb-10 max-w-2xl">
            <div className="label-overline mb-3">Plan comparison</div>
            <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight leading-tight mb-3">
              What you get on each plan.
            </h2>
            <p className="text-sm text-ink-muted leading-relaxed">
              Every plan runs the same sixty-point inspection. The Pro and
              Agency tiers unlock the prioritised fix-list, the branded PDF,
              and the lead-gen CTAs.
            </p>
          </div>

          <div className="surface-card overflow-hidden">
            {/* Sticky-style header row */}
            <div className="grid grid-cols-[1.7fr_repeat(3,1fr)] border-b border-border bg-[hsl(var(--surface-alt))]">
              <div className="px-5 py-4 label-overline">Feature</div>
              {[
                { name: "Free",   price: "$0",  period: "forever" },
                { name: "Pro",    price: "$19", period: "/ 30 days", accent: true },
                { name: "Agency", price: "$49", period: "/ 30 days" },
              ].map((c) => (
                <div key={c.name} className={`px-4 py-4 text-center border-l border-border ${c.accent ? "bg-brand/[0.06]" : ""}`}>
                  <div className="label-overline mb-1">{c.name}</div>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="num-display text-2xl font-black">{c.price}</span>
                    <span className="text-[10px] text-ink-muted mono">{c.period}</span>
                  </div>
                </div>
              ))}
            </div>

            {COMPARE_GROUPS.map((group, gi) => (
              <div key={group.title} data-testid={`compare-group-${gi}`}>
                <div className="grid grid-cols-[1.7fr_repeat(3,1fr)] bg-[hsl(var(--surface-alt))] border-y border-border">
                  <div className="px-5 py-2.5 mono text-[10px] tracking-[0.22em] font-bold text-brand uppercase col-span-4">
                    {group.title}
                  </div>
                </div>
                {group.rows.map(([label, free, pro, agency], i) => (
                  <div
                    key={label}
                    className={`grid grid-cols-[1.7fr_repeat(3,1fr)] items-center ${i !== group.rows.length - 1 ? "border-b border-border" : ""}`}
                  >
                    <div className="px-5 py-3 text-sm text-ink">{label}</div>
                    <div className="px-4 py-3 text-center border-l border-border">
                      <CompareCell value={free} />
                    </div>
                    <div className="px-4 py-3 text-center border-l border-border bg-brand/[0.04]">
                      <CompareCell value={pro} />
                    </div>
                    <div className="px-4 py-3 text-center border-l border-border">
                      <CompareCell value={agency} />
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* CTA row */}
            <div className="grid grid-cols-[1.7fr_repeat(3,1fr)] border-t border-border">
              <div className="px-5 py-4" />
              {[
                { plan: "free",   label: "Start free" },
                { plan: "pro",    label: "Get Pro",    accent: true },
                { plan: "agency", label: "Go Agency" },
              ].map((c) => (
                <div key={c.plan} className={`px-3 py-4 text-center border-l border-border ${c.accent ? "bg-brand/[0.06]" : ""}`}>
                  <Link
                    to={ctaTo}
                    data-testid={`compare-cta-${c.plan}`}
                    className={`inline-flex items-center justify-center gap-1.5 w-full text-xs font-semibold py-2 rounded-sm transition-colors ${
                      c.accent
                        ? "bg-ink text-white hover:bg-brand"
                        : "border border-ink text-ink hover:bg-ink hover:text-white"
                    }`}
                  >
                    {c.label} <ArrowRight size={12} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="label-overline mb-2">Pay-as-you-go</div>
              <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight">Audit credit packs</h2>
            </div>
            <span className="text-sm text-ink-muted">Credits never expire</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {CREDITS.map((c) => (
              <div key={c.count} data-testid={`pricing-credits-${c.count}`}
                className="surface-card p-6 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="num-display text-5xl">{c.count}</div>
                  {c.best && <span className="mono text-[10px] tracking-wider px-2 py-1 border border-success text-success rounded-sm">{c.best}</span>}
                </div>
                <div className="text-sm text-ink-muted mb-4">audit credits</div>
                <div className="flex items-baseline justify-between mb-5">
                  <span className="num-display text-2xl">{c.price}</span>
                  <span className="text-xs text-ink-muted mono">{c.per}/audit</span>
                </div>
                <Link to={ctaTo}
                  className="w-full py-2.5 text-center rounded-sm font-semibold border border-ink text-ink hover:bg-ink hover:text-white transition-colors">
                  Buy
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 gap-8 items-end">
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            Free forever. <br /> Upgrade when you outgrow it.
          </h2>
          <div className="md:text-right">
            <Link to={user ? "/app/dashboard" : "/register"} data-testid="pricing-footer-cta"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-4 rounded-sm font-semibold hover:bg-brand hover:text-white transition-colors">
              Create your free account <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
